"use client";

import React, { useState, useEffect } from "react";
import { Search, Menu, X, Globe, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground font-sans">Preparing the catch...</div>;
  }
  if (error) {
    return <div className="p-4 text-sm text-destructive font-sans">Error loading selection.</div>;
  }
  if (!products || products.length === 0) {
    return <div className="p-4 text-sm text-muted-foreground font-sans">No seafood found in this village.</div>;
  }
  return <div data-section-id="24973" className="max-h-[300px] overflow-y-auto border-t border-border">
      {products.map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 p-3 hover:bg-accent transition-colors" data-index={i}>
          {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} className="h-10 w-10 rounded-md object-cover bg-muted" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-foreground font-serif">{product.title}</p>
            <p className="text-xs text-muted-foreground font-sans">
              {product.price ? `$${product.price}` : ""}
            </p>
          </div>
        </Link>)}
    </div>;
}
function SearchDropdown({
  onClose
}: {
  onClose: () => void;
}) {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <div className="absolute right-0 top-full mt-2 w-[320px] sm:w-[400px] bg-background/95 backdrop-blur-md border border-secondary/20 rounded-xl shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
      <div className="p-3">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="relative">
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search our fresh selection..." className="w-full border-none focus-visible:ring-1 focus-visible:ring-secondary" />
          </div>
          {hasSearchQuery && <SearchResults />}
        </EcommerceProductsProvider>
      </div>
    </div>;
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium tracking-wide text-foreground hover:text-secondary transition-colors relative group py-2 font-sans">
                {item.label}
                <span className="absolute left-0 bottom-0 w-0 h-[1.5px] bg-secondary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  return <div className="flex flex-col gap-4 p-6">
      {loading ? <div className="space-y-4 animate-pulse">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted rounded-md" data-index={i} />)}
        </div> : <nav className="flex flex-col space-y-4">
          {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-secondary flex items-center justify-between border-b border-border pb-2 font-serif" onClick={onClose} data-index={i}>
              {item.label}
              <ChevronRight className="size-4 text-secondary" />
            </Link>)}
        </nav>}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="coastal-header" className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-secondary/40 shadow-md" : "bg-background border-b border-secondary/20"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/revitaltrax_logo.png" alt="Village Seafood Logo" className="h-10 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="22" className="w-full flex justify-center">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-2 sm:gap-4">
          {}
          <div className="hidden sm:block">
            <Select defaultValue="en">
              <SelectTrigger className="w-[70px] h-9 border-none bg-transparent shadow-none focus:ring-0 px-2 gap-1 text-foreground font-sans">
                <Globe className="size-4 text-secondary" />
                <SelectValue placeholder="EN" />
              </SelectTrigger>
              <SelectContent align="end" className="min-w-[80px] bg-background/95 backdrop-blur-md border-secondary/20">
                <SelectItem value="en">EN</SelectItem>
                <SelectItem value="es">ES</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {}
          <div className="relative">
            <Button variant="ghost" size="icon" className="text-foreground hover:text-secondary hover:bg-transparent transition-colors" onClick={() => setIsSearchOpen(!isSearchOpen)} aria-label="Search">
              {isSearchOpen ? <X className="size-5" /> : <Search className="size-5" />}
            </Button>
            {isSearchOpen && <SearchDropdown onClose={() => setIsSearchOpen(false)} />}
          </div>

          {}
          <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-secondary hover:bg-transparent transition-colors [&_span]:bg-destructive [&_span]:text-destructive-foreground" />

          {}
          <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-secondary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden fixed inset-0 top-20 bg-background z-40 overflow-y-auto border-t border-secondary/20 animate-in slide-in-from-top-5 duration-300">
          <MenuProvider menu_id="22">
            <MobileMenu onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-6 border-t border-secondary/10 mt-auto">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm font-medium text-foreground/70 font-sans">Language:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-8 px-4 border-secondary/30 text-secondary hover:bg-secondary/10">EN</Button>
                <Button variant="ghost" size="sm" className="h-8 px-4 text-foreground/60">ES</Button>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}